import React, { ChangeEvent, FC, useEffect } from 'react'
import { FieldErrors, UseFormMethods } from 'react-hook-form'
import {
  IContractForm,
  IDirectFrontlineContractFormField,
} from '../../interfaces/IContracts'
import { FIELD_PARENT_TOKENS, prepareOptions } from '../../utils/contracts'
import { CONTRACT_FIELDS_WITH_DETAILS_SECTION } from '../../constants/contracts'
import { ToggleSection } from '../common/ui/ToggleSection'
import { ContractFieldToggleContent } from '../contracts/ContractFieldToggleContent'
import SelectInput from '../form/SelectInput'
import Checkbox from '../form/Checkbox'

interface ContractFormFieldProps {
  field: IDirectFrontlineContractFormField
  data: IContractForm
  register: UseFormMethods['register']
  errors: FieldErrors
  watch?: UseFormMethods['watch']
  setValue?: UseFormMethods['setValue']
  /**
   * Names of the sibling checkbox fields that must be unchecked when this
   * field gets checked. Only passed down by a parent that enforces
   * "one child at a time" (the Frontpro special case).
   */
  exclusiveSiblingNames?: string[]
}

export const ContractFormField: FC<ContractFormFieldProps> = ({
  field,
  data,
  register,
  errors,
  watch,
  setValue,
  exclusiveSiblingNames,
}) => {
  const { id, label, options, mandatory, subfields, isCheckbox } = field

  const fieldName = `field${id}`
  const isChecked = watch?.(fieldName)

  const isFrontproSpecialCase = label.includes(FIELD_PARENT_TOKENS[2])
  const isRemiseSpecialCase = label.includes(FIELD_PARENT_TOKENS[3])
  const dependencyFieldNames = isRemiseSpecialCase
    ? (data?.fields ?? [])
        .filter(
          (f: any) =>
            FIELD_PARENT_TOKENS.some((tok) => f.label.includes(tok)) &&
            !f.label.includes(FIELD_PARENT_TOKENS[3])
        )
        .map((f: any) => `field${f.id}`)
    : []

  const dependencyValues = dependencyFieldNames.map((name) => watch?.(name))
  const allDependenciesChecked =
    dependencyFieldNames.length > 0 && dependencyValues.every(Boolean)

  useEffect(() => {
    if (
      !isRemiseSpecialCase ||
      !setValue ||
      dependencyFieldNames.length === 0
    ) {
      return
    }
    setValue(fieldName, allDependenciesChecked)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRemiseSpecialCase, setValue, fieldName, allDependenciesChecked])

  useEffect(() => {
    if (!isCheckbox || !setValue) return

    const isParent = FIELD_PARENT_TOKENS.some((tok) =>
      field.label.includes(tok)
    )

    if (isParent && options.length) {
      const matchingOption = options.find((opt) =>
        label.includes(opt.label.split(':')[0])
      )

      if (!matchingOption) return

      const matchingOptionFieldName = `field${matchingOption.id}`
      register(matchingOptionFieldName)
      setValue(matchingOptionFieldName, isChecked)
    }
  }, [
    isChecked,
    isCheckbox,
    setValue,
    fieldName,
    label,
    options,
    register,
    field.label,
  ])

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.checked || !setValue || !exclusiveSiblingNames?.length) {
      return
    }
    exclusiveSiblingNames.forEach((siblingName) => setValue(siblingName, false))
  }

  const detailsSection = CONTRACT_FIELDS_WITH_DETAILS_SECTION[
    data?.label
  ]?.fields?.filter(({ fieldLabel }) => label === fieldLabel)

  return (
    <div className="mb2">
      <div className="dInlineFlex alignCenter">
        {isCheckbox && (
          <Checkbox
            name={fieldName}
            id={id.toString()}
            register={mandatory ? register({ required: true }) : register}
            hasLabel={false}
            error={errors[fieldName]}
            disabled={isRemiseSpecialCase}
            onChange={handleCheckboxChange}
          />
        )}
        <label
          htmlFor={id.toString()}
          className="textPrimary fontRegular pb05 pointer"
        >
          {`${label}${mandatory ? '*' : ''}`}
        </label>
      </div>

      {!!detailsSection?.length &&
        detailsSection.map(({ fieldLabel, buttonLabel, itemsList }) => (
          <ToggleSection
            key={fieldLabel}
            buttonLabel={`form.contract.button.${buttonLabel}.label`}
          >
            <ContractFieldToggleContent
              buttonLabel={buttonLabel}
              itemsList={itemsList}
            />
          </ToggleSection>
        ))}

      {!isCheckbox && (
        <SelectInput
          name={fieldName}
          id={id.toString()}
          register={mandatory ? register({ required: true }) : register}
          renderLabel={false}
          options={prepareOptions(options)}
          error={errors[`field${id}`]}
          defaultPlaceholder={true}
        />
      )}

      {subfields && isChecked && (
        <div className="ml3">
          {subfields.map((subfield: IDirectFrontlineContractFormField) => {
            // Frontpro: only one checkbox child may be selected at a time.
            const siblingNames =
              isFrontproSpecialCase && subfield.isCheckbox
                ? subfields
                    .filter((s) => s.isCheckbox && s.id !== subfield.id)
                    .map((s) => `field${s.id}`)
                : undefined

            return (
              <ContractFormField
                key={subfield.id}
                field={subfield}
                data={data}
                register={register}
                errors={errors}
                watch={watch}
                setValue={setValue}
                exclusiveSiblingNames={siblingNames}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}
